import React, {useCallback, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import {callBackendApiGet} from "../lib/api";
import debounce from "lodash/debounce";
import {inputLabelSx, textFieldSx, textFieldSxAcneConcern} from "../lib/uiformatting";
import logDrtEvent from "../lib/logging";

const debouncedSearch = debounce(async (searchTerm, callback) => {
    if (!searchTerm) {
        callback([]);
        return;
    }
    try {
        const response = await callBackendApiGet(`/medications?search=${encodeURIComponent(searchTerm)}`);
        const data = await response.json();
        callback(data.medications || []);
    } catch (error) {
        await logDrtEvent("Error fetching medications", {error: error.toString(), searchTerm});
        callback([]);
    }
}, 300);

const durationOptions = [
    { value: "Currently on it", label: "Currently on it" },
    { value: "Previously - days/weeks", label: "Previously - days/weeks" },
    { value: "Previously - months or more", label: "Previously - months or more" }
];

const helpOptions = [
    { value: "Helped a lot", label: "Helped a lot" },
    { value: "Helped a little", label: "Helped a little" },
    { value: "Didn't help", label: "Didn't help" }
];

const dosageOptionsSpironolactone = [
    { value: "25mg", label: "25mg" },
    { value: "50mg", label: "50mg" },
    { value: "100mg", label: "100mg" },
    { value: "Not sure", label: "Not sure" }
];

const dosageOptionsTretinoinTazarotene = [
    { value: "0.025%", label: "0.025%" },
    { value: "0.05%", label: "0.05%" },
    { value: "0.1%", label: "0.1%" },
    { value: "Not sure", label: "Not sure" }
];

const commonMedications = [
    { value: "Accutane", label: "Accutane" },
    { value: "Azelaic Acid", label: "Azelaic Acid" },
    { value: "Benzoyl Peroxide", label: "Benzoyl Peroxide" },
    { value: "Clindamycin", label: "Clindamycin" },
    { value: "Doxycycline", label: "Doxycycline" },
    { value: "Spironolactone", label: "Spironolactone" },
    { value: "Tretinoin/Tazarotene", label: "Tretinoin/Tazarotene" },
    { value: "Other", label: "Other" }
];

const hairLossMedications = [
    { value: "Minoxidil - oral", label: "Minoxidil - oral" },
    { value: "Minoxidil - topical", label: "Minoxidil - topical" },
    { value: "Finasteride - oral", label: "Finasteride - oral" },
    { value: "Finasteride - topical", label: "Finasteride - topical" },
    { value: "Dutasteride - oral", label: "Dutasteride - oral" },
    { value: "Dutasteride - topical", label: "Dutasteride - topical" },
    { value: "Clobetasol", label: "Clobetasol" },
    { value: "Other", label: "Other" }
];

const psoriasisMedications = [
    { value: "Clobetasol", label: "Clobetasol" },
    { value: "Ketoconazole", label: "Ketoconazole" },
    { value: "Fluocinonide", label: "Fluocinonide" },
    { value: "Skyrizi", label: "Skyrizi" },
    { value: "Taltz", label: "Taltz" },
    { value: "Tremfya", label: "Tremfya" },
    { value: "Other", label: "Other" }
];

const eczemaMedications = [
    { value: "Triamcinolone", label: "Triamcinolone" },
    { value: "Tacrolimus", label: "Tacrolimus" },
    { value: "Ketoconazole", label: "Ketoconazole" },
    { value: "Hydrocortisone (topical)", label: "Hydrocortisone (topical)" },
    { value: "Oral prednisone", label: "Oral prednisone" },
    { value: "Dupixent", label: "Dupixent" },
    { value: "Other", label: "Other" }
];

export default function MedicationForm({ medications, setMedications, onCompletedMedicationsChange, onIncompleteMedicationsChange, initialCompletedMedications = [] }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const user = useSelector((state) => state.user);
    const isAcneConcern = user?.concern === "Acne";
    const isHairLossConcern = user?.concern === "Hair loss";
    const isPsoriasisConcern = user?.concern === "Psoriasis";
    const isEczemaConcern = user?.concern === "Eczema / Atopic Dermatitis";
    const [errors, setErrors] = useState(medications.map(() => ({ name: true, duration: true, help: true, dosage: true })));
    const [showOtherInput, setShowOtherInput] = useState(medications.map(() => false));
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [completedMedications, setCompletedMedications] = useState(initialCompletedMedications);
    const [incompleteMedications, setIncompleteMedications] = useState(medications);
    const [showDuration, setShowDuration] = useState(!isAcneConcern && !isHairLossConcern && !isPsoriasisConcern && !isEczemaConcern);
    const [showHelpfulness, setShowHelpfulness] = useState(!isAcneConcern && !isHairLossConcern && !isPsoriasisConcern && !isEczemaConcern);
    const [showDosage, setShowDosage] = useState(false);
    const [showAddButton, setShowAddButton] = useState(false);

    // Define hasIncompleteMedication function before it's used in useEffect
    const hasIncompleteMedication = useCallback(() => {
        if (medications.length === 0) return false;

        if (isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern) {
            // For Acne, Hair loss, Psoriasis, and Eczema patients, only check the first medication (progressive disclosure)
            const currentMed = medications[0];
            const hasAnyField = currentMed.name || currentMed.duration || currentMed.help ||
                               currentMed.dosage || (currentMed.name === "Other" && currentMed.customName);

            // If no fields are filled, it's not incomplete (form is empty)
            if (!hasAnyField) return false;

            // If fields are filled, check if the medication is complete and ready to be added
            const hasBasicFields = currentMed.name &&
                                  currentMed.duration &&
                                  currentMed.help &&
                                  (currentMed.name !== "Other" || currentMed.customName);

            // For Spironolactone and Tretinoin/Tazarotene, also require dosage
            const isComplete = currentMed.name === "Spironolactone" || currentMed.name === "Tretinoin/Tazarotene"
                ? hasBasicFields && currentMed.dosage
                : hasBasicFields;

            // If form is complete but medication hasn't been added yet, it's incomplete
            return isComplete;
        } else {
            // For non-Acne patients, check all medications
            return medications.some(med => {
                // Check if any field is filled
                const hasAnyField = med.name || med.duration || med.help ||
                                   med.dosage || (med.name === "Other" && med.customName);

                // If no fields are filled, it's not incomplete
                if (!hasAnyField) return false;

                const hasBasicFields = med.name &&
                                      med.duration &&
                                      med.help &&
                                      (med.name !== "Other" || med.customName);

                // For Spironolactone and Tretinoin/Tazarotene, also require dosage
                const isComplete = med.name === "Spironolactone" || med.name === "Tretinoin/Tazarotene"
                    ? hasBasicFields && med.dosage
                    : hasBasicFields;

                // If form is incomplete, return true (has incomplete medication)
                return !isComplete;
            });
        }
    }, [medications, isAcneConcern, isHairLossConcern, isPsoriasisConcern, isEczemaConcern]);

    // Check if there's at least one complete medication (for non-Acne users)
    useCallback(() => {
        if (isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern) {
            // For Acne, Hair loss, Psoriasis, and Eczema patients, use completedMedications count
            return completedMedications.length > 0;
        } else {
            // For non-Acne patients, check if any medication in the form is complete
            return medications.some(med => {
                const hasBasicFields = med.name &&
                    med.duration &&
                    med.help &&
                    (med.name !== "Other" || med.customName);

                // For Spironolactone, also require dosage
                return med.name === "Spironolactone"
                    ? hasBasicFields && med.dosage
                    : hasBasicFields;
            });
        }
    }, [medications, completedMedications, isAcneConcern, isHairLossConcern, isPsoriasisConcern, isEczemaConcern]);

    // Initialize completed medications only once
    useEffect(() => {
        setCompletedMedications(initialCompletedMedications);
    }, []); // Only run once on mount

    useEffect(() => {
        const medicationsWithIds = medications.map((med, index) => ({
            ...med,
            id: med.id || `med-${index}-${Date.now()}`,
            timestamp: med.timestamp || Date.now()
        }));

        // Update medications if any were missing IDs
        if (medicationsWithIds.some(med => !medications.find(original => original.id === med.id))) {
            setMedications(medicationsWithIds);
            return; // Let useEffect run again with updated medications
        }

        const newErrors = medicationsWithIds.map((med) => ({
            name: !med.name || (med.name === "Other" && !med.customName),
            duration: !med.duration,
            help: !med.help,
            dosage: med.name === "Spironolactone" || med.name === "Tretinoin/Tazarotene" ? !med.dosage : false
        }));

        const newShowOtherInput = medicationsWithIds.map((med) => med.name === "Other");

        // Update all states in a single batch
        setIncompleteMedications(medicationsWithIds);
        setErrors(newErrors);
        setShowOtherInput(newShowOtherInput);
    }, [medications]);

    // Notify parent component about incomplete medications
    useEffect(() => {
        if (onIncompleteMedicationsChange) {
            const hasIncomplete = hasIncompleteMedication();
            // Use setTimeout to defer the update to avoid updating during render
            const timeoutId = setTimeout(() => {
                onIncompleteMedicationsChange(hasIncomplete);
            }, 0);
            return () => clearTimeout(timeoutId);
        }
    }, [medications, onIncompleteMedicationsChange, hasIncompleteMedication]);

    // Notify parent about complete medications (consolidated for both Acne/Hair loss and non-Acne users)
    useEffect(() => {
        if (onCompletedMedicationsChange) {
            const completeMedications = (isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern)
                ? completedMedications
                : medications.filter(med => {
                    const hasBasicFields = med.name &&
                                          med.duration &&
                                          med.help &&
                                          (med.name !== "Other" || med.customName);

                    // For Spironolactone and Tretinoin/Tazarotene, also require dosage
                    return med.name === "Spironolactone" || med.name === "Tretinoin/Tazarotene"
                        ? hasBasicFields && med.dosage
                        : hasBasicFields;
                });

            // Use setTimeout to defer the update to avoid updating during render
            const timeoutId = setTimeout(() => {
                onCompletedMedicationsChange(completeMedications);
            }, 0);
            return () => clearTimeout(timeoutId);
        }
    }, [medications, completedMedications, isAcneConcern, isHairLossConcern, isPsoriasisConcern, isEczemaConcern, onCompletedMedicationsChange]);

    const isCurrentMedicationComplete = useCallback(() => {
        if (medications.length === 0) return false;
        const currentMed = medications[0]; // We only work with the first medication
        const hasBasicFields = currentMed.name &&
                              currentMed.duration &&
                              currentMed.help &&
                              (currentMed.name !== "Other" || currentMed.customName);

        // For Spironolactone, also require dosage
        if (currentMed.name === "Spironolactone" || currentMed.name === "Tretinoin/Tazarotene") {
            return hasBasicFields && currentMed.dosage;
        }

        return hasBasicFields;
    }, [medications]);

    const handleAddMedication = useCallback(() => {
        if (!isCurrentMedicationComplete()) return;

        const currentMed = medications[0];
        const completedMed = {
            ...currentMed,
            // If it's "Other", use the customName as the actual name
            name: currentMed.name === "Other" && currentMed.customName ? currentMed.customName : currentMed.name,
            timestamp: Date.now()
        };

        // Batch all state updates together
        const newMedication = {
            name: "",
            duration: "",
            help: "",
            timestamp: Date.now(),
            id: `med-${Date.now()}`
        };

        // Update all states in a single batch to prevent multiple re-renders
        setCompletedMedications(prev => [completedMed, ...prev]);
        setMedications([newMedication]);
        setErrors([{ name: true, duration: true, help: true, dosage: true }]);
        setShowOtherInput([false]);

        // Reset progressive disclosure based on concern type
        if (isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern) {
            // For Acne, Hair loss, Psoriasis, and Eczema patients, reset to show only medication section
            setShowDuration(false);
            setShowHelpfulness(false);
            setShowDosage(false);
            setShowAddButton(false);
        } else {
            // For non-Acne patients, show all sections by default
            setShowDuration(true);
            setShowHelpfulness(true);
            setShowDosage(false);
            setShowAddButton(false);
        }
    }, [medications, isCurrentMedicationComplete, isAcneConcern, isHairLossConcern, isPsoriasisConcern, isEczemaConcern]);

    const handleAddNewMedication = () => {
        const newMedication = {
            name: "",
            duration: "",
            help: "",
            dosage: "",
            timestamp: Date.now(),
            id: `med-${Date.now()}`
        };
        setMedications([...medications, newMedication]);
        setErrors([...errors, { name: true, duration: true, help: true, dosage: true }]);
        setShowOtherInput([...showOtherInput, false]);
    };

    const handleDeleteCompletedMedication = (cardId) => {
        // Remove the medication from the main medications array (if it exists there)
        const updatedMedications = medications.filter(med => med.id !== cardId);
        setMedications(updatedMedications);

        // Remove it from completedMedications directly for immediate UI update
        setCompletedMedications(prev => prev.filter(med => med.id !== cardId));

        // Notify parent component of the change
        if (onCompletedMedicationsChange) {
            const updatedCompleted = completedMedications.filter(med => med.id !== cardId);
            onCompletedMedicationsChange(updatedCompleted);
        }
    };

    const handleChange = useCallback((index, field, value) => {
        // Use functional updates to prevent unnecessary re-renders
        setMedications(prev => {
            const newMedications = [...prev];
            const medIndex = index; // Since we only have one medication now

            if (medIndex >= newMedications.length) return prev;

            const updatedMedication = { ...newMedications[medIndex] };

            if (field === "name" && value !== "Other") {
                updatedMedication[field] = value;
                updatedMedication.customName = "";
            } else if (field === "name" && value === "Other") {
                updatedMedication[field] = value;
                updatedMedication.customName = updatedMedication.customName || "";
            } else if (field === "customName") {
                updatedMedication.customName = value;
            } else {
                updatedMedication[field] = value;
            }

            newMedications[medIndex] = updatedMedication;

            // Handle progressive disclosure logic here with the updated medication
            // Only apply progressive disclosure for Acne, Hair loss, Psoriasis, and Eczema patients
            if (isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern) {
                if (field === "name" && value && value !== "") {
                    // Show the duration section when medication is selected
                    setShowDuration(true);
                    setShowHelpfulness(false); // Reset helpfulness section
                    setShowDosage(false); // Reset dosage section
                    setShowAddButton(false); // Reset add button
                } else if (field === "duration" && value && value !== "") {
                    // Show the helpfulness section when duration is selected
                    setShowHelpfulness(true);
                    setShowAddButton(false); // Reset add button
                } else if (field === "help" && value && value !== "") {
                    // For Spironolactone, show dosage section; otherwise show add button
                    if (updatedMedication.name === "Spironolactone" || updatedMedication.name === "Tretinoin/Tazarotene") {
                        setShowDosage(true);
                        setShowAddButton(false);
                    } else {
                        setShowAddButton(true);
                    }
                } else if (field === "dosage" && value && value !== "") {
                    setShowAddButton(true);
                } else if (field === "customName" && value && value !== "") {
                    // Show duration section when custom medication name is entered
                    setShowDuration(true);
                    setShowHelpfulness(false); // Reset helpfulness section
                    setShowDosage(false); // Reset dosage section
                    setShowAddButton(false); // Reset add button
                }
            } else {
                // For non-Acne patients, show all sections by default
                setShowDuration(true);
                setShowHelpfulness(true);
                setShowDosage(false); // Only show dosage for Spironolactone
                setShowAddButton(false); // No add button for non-Acne
            }

            return newMedications;
        });

        // Handle "Other" selection for medication name
        if (field === "name") {
            setShowOtherInput(prev => {
                const newShowOtherInput = [...prev];
                newShowOtherInput[index] = value === "Other";
                return newShowOtherInput;
            });
        }


        // Update errors
        setErrors(prev => {
            const newErrors = [...prev];
            if (field === "name") {
                newErrors[index] = {
                    ...newErrors[index],
                    [field]: value === "" || (value === "Other" && !value)
                };
            } else if (field === "customName") {
                newErrors[index] = {
                    ...newErrors[index],
                    name: value === ""
                };
            } else {
                newErrors[index] = {
                    ...newErrors[index],
                    [field]: value === ""
                };
            }
            return newErrors;
        });
    }, [isAcneConcern, isHairLossConcern, isPsoriasisConcern, isEczemaConcern]);

    const handleSearch = (searchTerm) => {
        setLoading(true);
        debouncedSearch(searchTerm, (results) => {
            setSearchResults(results);
            setLoading(false);
        });
    };

    const getMedicationDisplayName = (medication) => {
        if (medication.name === "Other" && medication.customName) {
            return medication.customName;
        }
        return medication.name;
    };

    const getCurrentMedicationName = () => {
        if (medications.length === 0) return "";
        const currentMed = medications[0];
        return getMedicationDisplayName(currentMed);
    };

    const getHelpfulnessLabel = (help) => {
        switch (help) {
            case "Helped a lot":
                return "Very helpful";
            case "Helped a little":
                return "Somewhat helpful";
            case "Didn't help":
                return "Not helpful";
            default:
                return help;
        }
    };

    const isMedicationSelected = (medValue) => {
        // Get all completed medication names (including custom names for "Other")
        const completedNames = completedMedications.map(med =>
            med.name === "Other" ? med.customName : med.name
        ).filter(Boolean);

        // Get currently selected medication in the form (if any)
        const currentFormMedication = incompleteMedications[0]?.name;
        const currentFormCustomName = incompleteMedications[0]?.customName;
        const currentFormDisplayName = currentFormMedication === "Other" ? currentFormCustomName : currentFormMedication;

        // Check if medication is already selected (completed or in current form)
        return completedNames.includes(medValue) || medValue === currentFormDisplayName;
    };

    return (
        <Box>
            {/* Incomplete Medications Form */}
            {((isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern) ? medications.slice(0, 1) : medications).map((medication, index) => (
                <Box
                    key={index}
                    sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "stretch" : "center",
                        mb: 2
                    }}
                >
                    {/* Close Icon - Only for non-Acne/Hair loss/Psoriasis/Eczema patients and when there are multiple medications */}
                    {!(isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern) && medications.length > 1 && (
                        <IconButton
                            onClick={() => {
                                const updatedMedications = medications.filter((_, i) => i !== index);
                                const updatedErrors = errors.filter((_, i) => i !== index);
                                const updatedShowOtherInput = showOtherInput.filter((_, i) => i !== index);
                                setMedications(updatedMedications);
                                setErrors(updatedErrors);
                                setShowOtherInput(updatedShowOtherInput);
                            }}
                            sx={{
                                color: theme.palette.error.main,
                                mb: isMobile ? 1 : 0,
                                mr: isMobile ? 0 : 1,
                                alignSelf: isMobile ? 'flex-start' : 'center'
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    )}
                    {(isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern) ? (
                        // New chip interface for Acne, Hair loss, Psoriasis, and Eczema
                        <FormControl
                            sx={{
                                flexGrow: 1,
                                mr: isMobile ? 0 : 1,
                                mb: isMobile ? 1 : 0,
                                minWidth: isMobile ? "100%" : 200,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    mb: 1,
                                    fontWeight: 800,
                                    color: errors[index]?.name ? theme.palette.text.main : theme.palette.text.primary,
                                }}
                            >
                                Medication Name *
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1.5,
                                mb: showOtherInput[index] ? 2 : 0
                            }}>
                                {(isHairLossConcern ? hairLossMedications : 
                                  isPsoriasisConcern ? psoriasisMedications :
                                  isEczemaConcern ? eczemaMedications :
                                  commonMedications).map((med) => {
                                    const isSelected = medication.name === med.value;
                                    const isAlreadySelected = med.value !== "Other" && isMedicationSelected(med.value);
                                    const isDisabled = isAlreadySelected && !isSelected;

                                    return (
                                        <Chip
                                            key={med.value}
                                            label={med.label}
                                            clickable={!isDisabled}
                                            onClick={isDisabled ? undefined : () => handleChange(index, "name", med.value)}
                                            variant={isSelected ? "filled" : "outlined"}
                                            sx={{
                                                fontSize: "0.75rem",
                                                fontWeight: isSelected ? 600 : 400,
                                                backgroundColor: isSelected
                                                    ? '#82B1FF33'
                                                    : isDisabled
                                                        ? theme.palette.grey[200]
                                                        : 'transparent',
                                                color: isSelected
                                                    ? theme.palette.primary.contrastText
                                                    : isDisabled
                                                        ? theme.palette.grey[500]
                                                        : theme.palette.text.primary,
                                                borderColor: isSelected
                                                    ? theme.palette.primary.main
                                                    : isDisabled
                                                        ? theme.palette.grey[300]
                                                        : theme.palette.grey[400],
                                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                opacity: isDisabled ? 0.6 : 1,
                                                '&:hover': {
                                                    backgroundColor: isDisabled
                                                        ? theme.palette.grey[200]
                                                        : isSelected
                                                            ? '#82B1FF33'
                                                            : theme.palette.action.hover,
                                                },
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                            {showOtherInput[index] && (
                                <Box sx={{ mt: 1 }}>
                                    <Autocomplete
                                        id={`med-autocomplete-${index}`}
                                        options={searchResults}
                                        value={medication.customName || ""}
                                        loading={loading}
                                        onInputChange={(event, newInputValue) => {
                                            handleSearch(newInputValue);
                                            handleChange(index, "customName", newInputValue);
                                        }}
                                        onChange={(e, newValue) => {
                                            handleChange(index, "customName", newValue || "");
                                        }}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props} sx={{ fontSize: "0.825rem", fontWeight: 400 }}>
                                                {option}
                                            </Box>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                sx={{
                                                    ...textFieldSxAcneConcern,
                                                    "& .MuiInputLabel-root": {
                                                        backgroundColor: theme.palette.background.paper,
                                                        padding: "0 32px",
                                                        transition: "all 0.2s ease-in-out",
                                                    },
                                                }}
                                                label="Enter medication name"
                                                placeholder="Search here..."
                                                variant="outlined"
                                                color="secondary"
                                                error={errors[index]?.name}
                                                helperText={
                                                    errors[index]?.name ? "This field is required" : " "
                                                }
                                                required
                                                InputLabelProps={{
                                                    sx: inputLabelSx,
                                                    "& .MuiInputLabel-shrink": {
                                                        backgroundColor: theme.palette.background.paper,
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            )}
                            <FormHelperText error={errors[index]?.name}>
                                {errors[index]?.name ? "Please select a medication" : " "}
                            </FormHelperText>
                        </FormControl>
                    ) : (
                        // Original Autocomplete interface for non-Acne
                        <FormControl
                            sx={{
                                ...textFieldSx,
                                flexGrow: 1,
                                mr: isMobile ? 0 : 1,
                                mb: isMobile ? 1 : 0,
                                minWidth: isMobile ? "100%" : 200,
                                "& .MuiInputLabel-root": {
                                    backgroundColor: theme.palette.background.paper,
                                    padding: "0 32px",
                                    transition: "all 0.2s ease-in-out",
                                },
                            }}
                        >
                            <Autocomplete
                                id={`med-autocomplete-${index}`}
                                options={searchResults}
                                value={medication.name || ""}
                                loading={loading}
                                onInputChange={(event, newInputValue) => {
                                    handleSearch(newInputValue);
                                    handleChange(index, "name", newInputValue);
                                }}
                                onChange={(e, newValue) => {
                                    handleChange(index, "name", newValue || "");
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        sx={{
                                            ...textFieldSx,
                                            "& .MuiInputLabel-root": {
                                                backgroundColor: theme.palette.background.paper,
                                                padding: "0 32px",
                                                transition: "all 0.2s ease-in-out",
                                            },
                                        }}
                                        label="Medication Name"
                                        placeholder="Search here..."
                                        variant="outlined"
                                        color="secondary"
                                        error={errors[index]?.name}
                                        helperText={
                                            errors[index]?.name ? "This field is required" : " "
                                        }
                                        required
                                        InputLabelProps={{
                                            sx: inputLabelSx,
                                            "& .MuiInputLabel-shrink": {
                                                backgroundColor: theme.palette.background.paper,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </FormControl>
                    )}

                    {showDuration && (
                        (isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern) ? (
                            // New chip interface for Acne
                            <FormControl
                                sx={{
                                    minWidth: isMobile ? "100%" : 200,
                                    mr: isMobile ? 0 : 1,
                                    mb: isMobile ? 1 : 0,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mb: 1,
                                        fontWeight: 800,
                                        color: errors[index]?.duration ? theme.palette.text.main : theme.palette.text.primary,
                                    }}
                                >
                                    Duration *
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1.5
                                }}>
                                    {durationOptions.map((option) => (
                                        <Chip
                                            key={option.value}
                                            label={option.label}
                                            clickable
                                            onClick={() => handleChange(index, "duration", option.value)}
                                            variant={medication.duration === option.value ? "filled" : "outlined"}
                                            sx={{
                                                fontSize: "0.75rem",
                                                fontWeight: medication.duration === option.value ? 600 : 400,
                                                backgroundColor: medication.duration === option.value
                                                    ? '#82B1FF33'
                                                    : 'transparent',
                                                color: medication.duration === option.value
                                                    ? theme.palette.primary.contrastText
                                                    : theme.palette.text.primary,
                                                borderColor: medication.duration === option.value
                                                    ? theme.palette.primary.main
                                                    : theme.palette.grey[400],
                                                '&:hover': {
                                                    backgroundColor: medication.duration === option.value
                                                        ? '#82B1FF33'
                                                        : theme.palette.action.hover,
                                                },
                                            }}
                                        />
                                    ))}
                                </Box>
                                <FormHelperText error={errors[index]?.duration}>
                                    {errors[index]?.duration ? "This field is required" : " "}
                                </FormHelperText>
                            </FormControl>
                        ) : (
                            // Old dropdown interface for non-Acne
                            <FormControl
                                sx={{
                                    ...textFieldSx,
                                    minWidth: isMobile ? "100%" : 200,
                                    mr: isMobile ? 0 : 1,
                                    mb: isMobile ? 1 : 0,
                                    "& .MuiInputLabel-root": {
                                        backgroundColor: theme.palette.background.paper,
                                        padding: "0 32px",
                                        transition: "all 0.2s ease-in-out",
                                    },
                                }}
                            >
                                <InputLabel
                                    id={`duration-label-${index}`}
                                    sx={{
                                        ...inputLabelSx,
                                        "& .MuiInputLabel-shrink": {
                                            backgroundColor: theme.palette.background.paper,
                                        },
                                    }}
                                >
                                    Duration
                                </InputLabel>
                                <Select
                                    labelId={`duration-label-${index}`}
                                    value={medication.duration}
                                    onChange={(e) => handleChange(index, "duration", e.target.value)}
                                    label="Duration"
                                    error={errors[index]?.duration}
                                    required
                                >
                                    {durationOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText error={errors[index]?.duration}>
                                    {errors[index]?.duration ? "This field is required" : " "}
                                </FormHelperText>
                            </FormControl>
                        )
                    )}

                    {showHelpfulness && (
                        (isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern) ? (
                            // New chip interface for Acne
                            <FormControl
                                sx={{
                                    minWidth: isMobile ? "100%" : 150,
                                    mr: isMobile ? 0 : 1,
                                    mb: isMobile ? 1 : 0,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mb: 1,
                                        fontWeight: 800,
                                        color: errors[index]?.help ? theme.palette.text.main : theme.palette.text.primary,
                                    }}
                                >
                                    Did it help? *
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1.5
                                }}>
                                    {helpOptions.map((option) => (
                                        <Chip
                                            key={option.value}
                                            label={option.label}
                                            clickable
                                            onClick={() => handleChange(index, "help", option.value)}
                                            variant={medication.help === option.value ? "filled" : "outlined"}
                                            sx={{
                                                fontSize: "0.75rem",
                                                fontWeight: medication.help === option.value ? 600 : 400,
                                                backgroundColor: medication.help === option.value
                                                    ? '#82B1FF33'
                                                    : 'transparent',
                                                color: medication.help === option.value
                                                    ? theme.palette.primary.contrastText
                                                    : theme.palette.text.primary,
                                                borderColor: medication.help === option.value
                                                    ? theme.palette.primary.main
                                                    : theme.palette.grey[400],
                                                '&:hover': {
                                                    backgroundColor: medication.help === option.value
                                                        ? '#82B1FF33'
                                                        : theme.palette.action.hover,
                                                },
                                            }}
                                        />
                                    ))}
                                </Box>
                                <FormHelperText error={errors[index]?.help}>
                                    {errors[index]?.help ? "This field is required" : " "}
                                </FormHelperText>
                            </FormControl>
                        ) : (
                            // Old dropdown interface for non-Acne
                            <FormControl
                                sx={{
                                    ...textFieldSx,
                                    minWidth: isMobile ? "100%" : 150,
                                    mr: isMobile ? 0 : 1,
                                    mb: isMobile ? 1 : 0,
                                    "& .MuiInputLabel-root": {
                                        backgroundColor: theme.palette.background.paper,
                                        padding: "0 32px",
                                        transition: "all 0.2s ease-in-out",
                                    },
                                }}
                            >
                                <InputLabel
                                    id={`help-label-${index}`}
                                    sx={{
                                        ...inputLabelSx,
                                        "& .MuiInputLabel-shrink": {
                                            backgroundColor: theme.palette.background.paper,
                                        },
                                    }}
                                >
                                    Did it help?
                                </InputLabel>
                                <Select
                                    labelId={`help-label-${index}`}
                                    value={medication.help}
                                    onChange={(e) => handleChange(index, "help", e.target.value)}
                                    label="Did it help?"
                                    error={errors[index]?.help}
                                    required
                                >
                                    {helpOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText error={errors[index]?.help}>
                                    {errors[index]?.help ? "This field is required" : " "}
                                </FormHelperText>
                            </FormControl>
                        )
                    )}

                    {showDosage && (
                        <FormControl
                            sx={{
                                minWidth: isMobile ? "100%" : 150,
                                mr: isMobile ? 0 : 1,
                                mb: isMobile ? 1 : 0,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    mb: 1,
                                    fontWeight: 800,
                                    color: errors[index]?.dosage ? theme.palette.text.main : theme.palette.text.primary,
                                }}
                            >
                                Dosage *
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1.5
                            }}>
                                {(medication.name === "Spironolactone" ? dosageOptionsSpironolactone : 
                                  medication.name === "Tretinoin/Tazarotene" ? dosageOptionsTretinoinTazarotene : 
                                  []).map((option) => (
                                    <Chip
                                        key={option.value}
                                        label={option.label}
                                        clickable
                                        onClick={() => handleChange(index, "dosage", option.value)}
                                        variant={medication.dosage === option.value ? "filled" : "outlined"}
                                        sx={{
                                            fontSize: "0.75rem",
                                            fontWeight: medication.dosage === option.value ? 600 : 400,
                                            backgroundColor: medication.dosage === option.value
                                                ? '#82B1FF33'
                                                : 'transparent',
                                            color: medication.dosage === option.value
                                                ? theme.palette.primary.contrastText
                                                : theme.palette.text.primary,
                                            borderColor: medication.dosage === option.value
                                                ? theme.palette.primary.main
                                                : theme.palette.grey[400],
                                            '&:hover': {
                                                backgroundColor: medication.dosage === option.value
                                                    ? '#82B1FF33'
                                                    : theme.palette.action.hover,
                                            },
                                        }}
                                    />
                                ))}
                            </Box>
                            <FormHelperText error={errors[index]?.dosage}>
                                {errors[index]?.dosage ? "This field is required" : " "}
                            </FormHelperText>
                        </FormControl>
                    )}
                </Box>
            ))}

            {/* Add Medication Button - Only for Acne, Hair loss, Psoriasis, and Eczema patients */}
            {(isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern) && showAddButton && (
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        startIcon={<CheckIcon />}
                        onClick={handleAddMedication}
                        color="secondary"
                        sx={{
                            borderColor: "piction.secondary",
                            backgroundColor: "piction.secondary",
                            color: "white",
                            '&:hover': {
                                backgroundColor: "piction.secondary",
                                borderColor: "piction.secondary",
                            }
                        }}
                    >
                        Add {getCurrentMedicationName()}
                    </Button>
                </Box>
            )}

            {/* Add New Medication Button - For non-Acne/Hair loss/Psoriasis/Eczema patients */}
            {!(isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern) && (
                <Box sx={{
                    mb: 3,
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    flex: '0 0 auto'
                }}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleAddNewMedication}
                        sx={{
                            fontFamily: "Inter, sans-serif",
                            fontWeight: 600,
                            minHeight: "clamp(40px, 6vh, 70px)",
                            fontSize: "clamp(10px, 2.5vh, 14px)",
                            maxWidth: "600px",
                            width: isMobile ? "100%" : "300px",
                            borderStyle: "solid",
                            borderWidth: "2px",
                            borderColor: "grayTones.piction",
                            backgroundColor: "white",
                            color: "black",
                            borderRadius: "25px",
                            '&:hover': {
                                backgroundColor: "rgba(34, 0, 187, 0.04)",
                                borderColor: "piction.highlight",
                                borderWidth: "2px",
                                color: "piction.highlight",
                            },
                            transition: 'all 0.3s ease',
                            margin: '0 auto',
                        }}
                    >
                        Add New Medication
                    </Button>
                </Box>
            )}

            {/* Completed Medications Cards - Only for Acne, Hair loss, Psoriasis, and Eczema users */}
            {(isAcneConcern || isHairLossConcern || isPsoriasisConcern || isEczemaConcern) && completedMedications.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                        Your Medications ({completedMedications.length})
                    </Typography>
                    {completedMedications.map((medication) => (
                        <Card
                            key={medication.id}
                            elevation={0}
                            sx={{
                                mb: 2,
                                backgroundColor: "piction.background",
                                borderRadius: 2,
                                borderWidth: "1px",
                                borderStyle: "solid",
                                borderColor: "grayTones.border",
                                position: 'relative',
                                '&:hover': {
                                    backgroundColor: "rgba(34, 0, 187, 0.04)",
                                    borderColor: "grayTones.border",
                                }
                            }}
                        >
                            <CardContent sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">{getMedicationDisplayName(medication)}</Typography>
                                    <IconButton
                                        onClick={() => handleDeleteCompletedMedication(medication.id)}
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            color: 'grayTones.main',
                                            '&:hover': {
                                                backgroundColor: 'grayTones.main + 20',
                                            }
                                        }}
                                        size="small"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Duration: {medication.duration}
                                </Typography>
                                {medication.dosage && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Dosage: {medication.dosage}
                                    </Typography>
                                )}
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Helpfulness: {getHelpfulnessLabel(medication.help)}</Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}
        </Box>
    );
}
